/**
 * BreadcrumbList structured data.
 *
 * Google renders a breadcrumb trail in place of the raw URL path in search
 * results when a page carries this markup, so `Vell > Travel eSIM > Japan`
 * replaces `vell.net > travel-esim > japan`. Without it Google splits the URL
 * itself, which is why slugs like `send-money-to-china` currently show up in
 * results verbatim.
 */
import { SITE_URL, SITE_NAME } from "@/lib/metadata";

export type Crumb = {
  name: string;
  /** Site-relative, e.g. "/travel-esim". Omitted on the current page. */
  path?: string;
};

/**
 * Sections that have a real index page, longest prefix winning.
 *
 * A crumb may only link to a URL that actually resolves — a trail pointing at
 * a 404 is worse than a shorter trail. There is no `/money-transfer`, `/legal`
 * or `/resources` index page, so pages under those prefixes deliberately get a
 * two-level trail (Home > page). Building a hub page for any of them is what
 * would earn the third level; do not add a section entry here before the page
 * behind it exists.
 */
const SECTIONS: readonly Required<Crumb>[] = [
  { name: "Blog", path: "/blog" },
  { name: "Careers", path: "/career" },
  { name: "Help Centre", path: "/frequently-asked-questions" },
  { name: "Travel eSIM", path: "/travel-esim" },
] as const;

/**
 * Short breadcrumb labels for every static page that carries a trail.
 *
 * Keyed by canonical path so the label and the URL live on one line and cannot
 * drift apart — the same duplication that produced the Morocco canonical typo.
 * These are deliberately not the pages' SEO titles: a crumb reads "China", not
 * "Send Money from Australia to China | AUD to CNY Transfers | Vell", because
 * Google truncates long crumb names.
 *
 * A path missing from this map simply gets no breadcrumb markup, which is the
 * safe failure. breadcrumbs.test.ts asserts every key here resolves to a real
 * page, so a renamed route surfaces as a failing test rather than as invalid
 * markup in production.
 */
const BREADCRUMB_LABELS: Readonly<Record<string, string>> = Object.freeze({
  // Core
  "/about-vell": "About Vell",
  "/about-us": "About Us",
  "/our-company": "Our Company",
  "/contact-us": "Contact Us",
  "/security-and-protection": "Security & Protection",
  "/sustainability": "Sustainability",
  "/download-app": "Download App",
  "/site-map": "Site Map",
  "/gift": "Promo Gift",

  // Products
  "/personal-account": "Personal Account",
  "/business-account": "Business Account",
  "/travel-esim": "Travel eSIM",
  "/travel-esim-comparison": "eSIM Comparison",
  "/travel-esim/installationguide": "Installation Guide",
  "/travel-esim/install-esim": "How to Install",
  "/travel-esim/activate-esim": "How to Activate",
  "/travel-esim/terms-and-conditions": "eSIM Terms & Conditions",

  // Help centre
  "/frequently-asked-questions": "Help Centre",
  "/frequently-asked-questions/about-vell": "About Vell",
  "/frequently-asked-questions/pricing-and-fees": "Pricing & Fees",
  "/frequently-asked-questions/scheduled-payments": "Scheduled Payments",
  "/frequently-asked-questions/sending-money": "Sending Money",
  "/frequently-asked-questions/transfer-time-and-tracking": "Transfer Times & Tracking",

  // Blog and careers
  "/blog": "Blog",
  "/career": "Careers",

  // Resources
  "/resources/currency-converter": "Currency Converter",
  "/resources/historical-rates": "Historical Rates",
  "/resources/international-money-transfer-comparison": "Transfer Comparison",
  "/resources/rateTracker": "Rate Tracker",
  "/resources/swift-code-finder": "SWIFT Code Finder",

  // Legal
  "/legal/terms-and-conditions": "Terms & Conditions",
  "/legal/privacy-policy": "Privacy Policy",
  "/legal/privacy-policy-cn": "Privacy Policy (Chinese)",
  "/legal/cookie-policy": "Cookie Policy",
  "/legal/personalised-ads": "Personalised Ads",
  "/legal/complaints-policy": "Complaints Policy",
  "/legal/promotional-offer-terms-and-conditions": "Promotional Offer Terms",
  "/legal/regulatory": "Regulatory",

  // Money transfer corridors
  "/money-transfer/send-money-to-china": "China",
  "/money-transfer/send-money-to-india": "India",
  "/money-transfer/send-money-to-nepal": "Nepal",
  "/money-transfer/send-money-to-new-zealand": "New Zealand",
  "/money-transfer/send-money-to-philippines": "Philippines",
  "/money-transfer/send-money-to-singapore": "Singapore",
  "/money-transfer/send-money-to-sri-lanka": "Sri Lanka",
  "/money-transfer/send-money-to-uae": "UAE",
  "/money-transfer/send-money-to-uk": "UK",
  "/money-transfer/send-money-to-usa": "USA",
  "/money-transfer/send-money-to-vietnam": "Vietnam",
  "/money-transfer/send-money-to-brazil": "Brazil",
  "/money-transfer/send-money-to-canada": "Canada",
  "/money-transfer/send-money-to-chile": "Chile",
  "/money-transfer/send-money-to-czech-republic": "Czech Republic",
  "/money-transfer/send-money-to-denmark": "Denmark",
  "/money-transfer/send-money-to-hong-kong": "Hong Kong",
  "/money-transfer/send-money-to-indonesia": "Indonesia",
  "/money-transfer/send-money-to-south-africa": "South Africa",
  "/money-transfer/send-money-to-norway": "Norway",
  "/money-transfer/send-money-to-mexico": "Mexico",
  "/money-transfer/send-money-to-morocco": "Morocco",
  "/money-transfer/send-money-to-malaysia": "Malaysia",
  "/money-transfer/send-money-to-poland": "Poland",
  "/money-transfer/send-money-to-sweden": "Sweden",
  "/money-transfer/send-money-to-thailand": "Thailand",
  "/money-transfer/send-usd-internationally": "Send USD Internationally",
});

/** Exposed for the test that checks every label points at a real page. */
export const BREADCRUMB_LABEL_PATHS = Object.keys(BREADCRUMB_LABELS);

/** Exposed for the test that checks every section index page exists. */
export const BREADCRUMB_SECTION_PATHS = SECTIONS.map((s) => s.path);

function findSection(path: string): Required<Crumb> | undefined {
  return SECTIONS.filter((s) => path.startsWith(`${s.path}/`)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];
}

/**
 * Builds the trail for a page: Home, the section index if one exists, then the
 * page itself. Returns null for the homepage and for anything that would
 * produce a single-item trail, since a breadcrumb of one is not worth emitting.
 */
export function buildBreadcrumbTrail({
  path,
  name,
}: {
  path: string;
  /** Omit for a static page and the label map supplies it. */
  name?: string;
}): Crumb[] | null {
  const leaf = name ?? BREADCRUMB_LABELS[path];
  if (path === "/" || !leaf?.trim()) return null;

  const trail: Crumb[] = [{ name: SITE_NAME, path: "/" }];

  const section = findSection(path);
  // A section index page is its own leaf, not its own parent.
  if (section && section.path !== path) trail.push(section);

  trail.push({ name: leaf.trim() });

  return trail;
}

/**
 * Serialises a trail to schema.org JSON-LD. The final item carries no `item`
 * URL, which is the documented pattern for the page currently being viewed.
 */
export function buildBreadcrumbSchema(trail: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      ...(crumb.path ? { item: `${SITE_URL}${crumb.path}` } : {}),
    })),
  };
}
